import { Grid } from "@mui/material";
import { withStyles, WithStyles } from "@mui/styles";
import * as coreApi from "api/Core";
import { FeedStyleClasses, TimelineStyleClasses } from "assets/jss/common-styles";
import { RequestIndicator } from "components/CallServer";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import MuiContainer from "components/MuiExtras/MuiContainer";
import WidgetDrawer from "components/WidgetDrawer";
import { GetUserProfileDto, UserProfileRelationDto } from "models/ApiModels";
import * as React from "react";
import ReactResizeDetector from "react-resize-detector";
import { RouteComponentProps } from 'react-router';
import Session from "services/Session";
import FeedUserProfilePublications from "views/Timeline/Components/FeedUserProfilePublications";
import UserEdit from "views/User/Components/UserEdit";
import UserProfileCardHeader from "views/User/Components/UserProfileCardHeader";
import UserFollowersDrawer from "views/User/Dialogs/UserFollowersDrawer";
import WidgetCredentials from "views/User/Widgets/WidgetCredentials";
import WidgetKnowsAbout from "views/User/Widgets/WidgetKnowsAbout";
import WidgetSocialNetworks from "views/User/Widgets/WidgetSocialNetworks";
import WidgetStatistics from "views/User/Widgets/WidgetStatistics";

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    dialog: any;
    editUser: boolean;
    lastError: any,
    loading: boolean;
    profile: GetUserProfileDto;
}

interface IProps {
}

interface IRouteParams {
    id: string;
}

type Props = IProps & RouteComponentProps<IRouteParams> & WithStyles<typeof TimelineStyleClasses>;

/////////////////////////////////////////////////////////////////////////////////////////////////

export class UserProfileViewer extends React.Component<Props, IState> {

    constructor(props: Props) {
        super(props);

        this.state = {
            dialog: null,
            editUser: false,
            lastError: null,
            loading: true,
            profile: null
        };
    }

    ///
    private onLoad(id: string) {

        coreApi.getUserProfile(id)
            .then((profile) => {
                this.setState({ profile: profile, loading: false });
            })
            .catch((lastError) => {
                this.setState({ lastError: lastError, loading: false });
            });
    }

    ///
    private handleRelationChange = (newRelation: UserProfileRelationDto) => {
        this.setState(current => {
            var newProfile = Object.assign({}, current.profile);
            newProfile.currentUserRelation = newRelation;
            return { ...current, profile: newProfile };
        });
    };

    ///
    private handleInfo = () => {
        this.setState({
            dialog: (
                <WidgetDrawer onClose={() => this.setState({ dialog: null })} >
                    {this.renderInfo()}
                </WidgetDrawer>
            )
        });
    };

    ///
    private handleFollowers = () => {
        this.setState({
            dialog: (
                <UserFollowersDrawer
                    userId={this.state.profile.userId}
                    username={this.state.profile.name}
                    onClose={() => this.setState({ dialog: null })}
                />
            )
        });
    };

    ///
    private handleEditProfile = () => {
        this.setState({
            dialog: null,
            editUser: true
        });
    };

    ///
    private renderInfo() {
        return (
            <>
                <WidgetCredentials userId={this.state.profile.userId} />
                <WidgetKnowsAbout userId={this.state.profile.userId} username={this.state.profile.name} />
                <WidgetSocialNetworks socialNetworks={this.state.profile.personal.socialNetworks} />
                <WidgetStatistics statistics={this.state.profile.statistics} />
            </>
        );
    }

    ///
    private renderEdit() {

        return (
            <UserEdit
                userId={this.state.profile.userId}
                onClose={
                    (reload) => {
                        this.setState({ editUser: false });
                    }
                }
            />
        );
    }

    ///
    private renderContent(width: number) {
        const { classes } = this.props;
        const { profile } = this.state;

        return (
            <Grid container spacing={1} direction="row" style={{ flexWrap: "nowrap" }}>
                <Grid item style={{ flexGrow: 1 }}>

                    <div className={classes.timelineContainer}>

                        <MuiContainer margin="normal">
                            <UserProfileCardHeader
                                profile={profile}
                                showOverflowMenu={true}
                                onRelationChange={this.handleRelationChange}
                                onEditProfile={this.handleEditProfile}
                                onFollowers={this.handleFollowers}
                                onInfoRequest={!profile.currentUserRelation.blockedByProfile && width < 920 ? this.handleInfo : null}
                            />
                        </MuiContainer>

                        {
                            !profile.currentUserRelation.blockedByProfile &&
                            <FeedUserProfilePublications
                                classes={classes}
                                userId={this.props.match.params.id}
                                fullView={false}
                            />
                        }
                    </div>
                </Grid>

                {
                    !profile.currentUserRelation.blockedByProfile &&
                    width > 919 &&
                    <Grid item style={{ width: 350 }}>
                        {this.renderInfo()}
                    </Grid>
                }
            </Grid>
        );
    }

    ///
    public componentDidMount() {
        this.onLoad(this.props.match.params.id || Session.user.id);
    }

    ///
    public componentDidUpdate(prevProps: Props) {
        if (prevProps.match.url != this.props.match.url) {
            this.onLoad(this.props.match.params.id);
        }
    }

    ///
    public render() {
        const { loading, lastError, dialog, editUser } = this.state;

        return (
            <>
                <ReactResizeDetector handleWidth refreshMode="debounce" refreshRate={0}>
                    {
                        (d: ReactResizeDetectorDimensions) =>

                            lastError != null
                                ? <RequestErrorReport error={lastError} />
                                : loading
                                    ? <RequestIndicator type="waiting" />
                                    : editUser
                                        ? this.renderEdit()
                                        : this.renderContent(d.width)
                    }
                </ReactResizeDetector>

                {dialog}
            </>
        );
    }
}

/////////////////////////////////////////////////////////////////////////////////////////////////

const withAllStyles = withStyles(
    (theme) => ({
        ...TimelineStyleClasses(theme),
        ...FeedStyleClasses(theme),
    }),
    { withTheme: true },
);

export default withAllStyles(UserProfileViewer);